import { useEffect, useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import Spinner from '../../components/Spinner';

import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import Tooltip from '@mui/material/Tooltip';

import Button from '@mui/material/Button';

import { useTheme } from '@mui/material/styles';

import { Context } from '../../AuthContext';
import { useCategory } from '../../CategoryContext';

import BackButton from '../../components/BackButton';
import PdfViewer from '../../components/PdfViewer';

import AlertDialog from '../../components/AlertDialog';
import { useDeleteContent } from './useDeleteContent';

const DetailPage = ({
  category,
  heading,
  field_icon,
  importantDate,
  externalLink,
  field_heading,
  url_icon,
}) => {
  const { updateCount, isEditor } = useCategory();
  const { handleDelete } = useDeleteContent();
  const [item, setItem] = useState({});
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState();

  const { vertical_id, id } = useParams();
  const navigate = useNavigate();

  const theme = useTheme();

  const { user } = useContext(Context);

  const activeButtonStyle = {
    fontSize: '16px',
    margin: '10px',
    width: '100%',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
    },
  };

  useEffect(() => {
    if (!user) return;

    const getItem = async () => {
      const item = await fetchItem();
      if (!item) return;
      setItem(item.data);
      console.log('item.data: ', item.data);
      document.title = item.data[heading] + ' - The Simple Platform';
      setShow(() => {
        if (category === 'user') {
          const matchedVertical = item.data.verticals?.find((v) => v.id === vertical_id);
          return matchedVertical?.show ?? true;
        } else {
          return item.data.show;
        }
      });
      setLoading(false);
    };
    getItem();
  }, [category, heading, id, user, vertical_id]);

  const fetchItem = async () => {
    const token = await user.getIdToken();
    let url;
    if (category !== 'tutorial')
      url =
        process.env.REACT_APP_FLASK_WEBSERVER +
        `get_${category}_details?id=${id}&vertical_id=${vertical_id}`;
    else
      url =
        process.env.REACT_APP_FLASK_WEBSERVER +
        'get_' +
        category +
        '_details?id=' +
        id +
        '&vertical_id=' +
        vertical_id;
    const itemRequest = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (itemRequest.status === 403) {
      navigate('/forbidden', { replace: true });
      return null;
    }
    const item = await itemRequest.json();

    if (category === 'user') {
      item.data['type'] = getUserType(item.data?.verticals || []);
    }

    const bookmark_status_endpoint =
      process.env.REACT_APP_FLASK_WEBSERVER + `get_bookmark_id/${item?.data?._id}`;
    const bookmarkRequest = await fetch(bookmark_status_endpoint, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const bookmark = bookmarkRequest.ok ? await bookmarkRequest.json() : { isBookmarked: false };

    const result = {
      ...item,
      data: {
        ...item.data,
        isBookmarked: bookmark.isBookmarked,
      },
    };

    return result;
  };

  function editContent(event) {
    event.stopPropagation();
    navigate('edit');
  }

  const hideContent = async (event) => {
    event.stopPropagation();
    if (!user) return;

    const isChecked = !show;
    let sideBarCategory = category === 'person' || category === 'user' ? 'people' : category + 's';
    let collection = category === 'user' ? 'users' : sideBarCategory;
    if (isChecked) {
      updateCount(sideBarCategory, 1);
    } else {
      updateCount(sideBarCategory, -1);
    }
    setShow(isChecked);
    const token = await user.getIdToken();

    const res = await fetch(
      process.env.REACT_APP_FLASK_WEBSERVER +
        'change_visibility?id=' +
        item._id +
        '&collection=' +
        collection +
        '&vertical_id=' +
        vertical_id,
      {
        method: 'PATCH',
        headers: {
          'Content-type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ show: isChecked }),
      }
    );
    await res.json();
  };

  const toggleBookmark = async (event, itemId) => {
    event.stopPropagation();
    setItem({ ...item, isBookmarked: !item.isBookmarked });

    let collection;
    if (category === 'person') {
      collection = 'people';
    } else {
      collection = category + 's';
    }

    try {
      let vid = vertical_id;
      const token = await user.getIdToken();

      await fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}toggle_bookmark`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: user.uid,
          item_id: itemId,
          category: collection,
          vertical_id: vid,
        }),
      });
    } catch (error) {
      console.error('Failed to toggle bookmark:', error);
    }
  };

  const getUserType = (user_verticals) => {
    let user_vertical = null;

    for (let vertical of user_verticals) {
      if (vertical.id == vertical_id) {
        user_vertical = vertical;
        break;
      }
    }

    if (user_vertical) {
      switch (user_vertical.status) {
        case 'Joined':
          if (user_vertical?.permission === 'Editor') return 'Editor';
          else return 'Member';
        case 'Created':
          return 'Creator';
        default:
          return 'User';
      }
    } else {
      return 'User';
    }
  };

  return loading ? (
    <Spinner />
  ) : (
    <div style={{ width: '75%', textAlign: 'left' }}>
      <BackButton />

      {category === 'tutorial' ? (
        <div className="detail-card" style={{ padding: '0px', marginTop: '10px' }}>
          <PdfViewer link={item?.link} />
        </div>
      ) : (
        <div>
          <h4 style={{ color: '#596274' }}>
            {item?.type ? item.type.toUpperCase() : category.toUpperCase()}
          </h4>
          <div className="detail-card">
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', paddingBottom: '20px' }}>
                {category === 'organization' &&
                  (item?.image_url ? (
                    <img
                      src={item.image_url}
                      alt="Organization"
                      style={{
                        height: '128px',
                        width: '128px',
                        marginRight: '20px',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <AccountBalanceOutlinedIcon
                      style={{ height: '128px', width: '128px', paddingRight: '20px' }}
                    />
                  ))}

                {['person', 'user'].includes(category) && (
                  <div>
                    {item?.image_url ? (
                      <img
                        src={item.image_url}
                        alt="Person"
                        style={{
                          height: '128px',
                          width: '128px',
                          marginRight: '20px',
                          objectFit: 'cover',
                          borderRadius: '50%',
                        }}
                      />
                    ) : (
                      <AccountCircleOutlinedIcon
                        style={{ height: '128px', width: '128px', paddingRight: '20px' }}
                      />
                    )}
                  </div>
                )}

                {(category === 'job' || category === 'event') && item?.image_url && (
                  <img
                    src={item.image_url}
                    alt="Person"
                    style={{
                      height: '128px',
                      width: '128px',
                      marginRight: '20px',
                      objectFit: 'cover',
                    }}
                  />
                )}

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <h2 className="detail-title">{item[heading]}</h2>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: 0,
                  margin: 0,
                }}
              >
                {isEditor && item?.status === 'approved' && (
                  <div style={{ display: 'flex', alignItems: 'center', padding: 0, margin: 0 }}>
                    {show ? (
                      <Tooltip title="Visible to Members">
                        <VisibilityOutlinedIcon
                          style={{ color: '#356AC3' }}
                          className="detail-icon clickable"
                          onClick={(event) => hideContent(event)}
                          aria-label="Content is visible"
                        />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Hidden To Members">
                        <VisibilityOffOutlinedIcon
                          style={{ color: '#356AC3' }}
                          className="detail-icon clickable"
                          onClick={(event) => hideContent(event)}
                          aria-label="Content is hidden"
                        />
                      </Tooltip>
                    )}
                    <FontAwesomeIcon
                      color="#356AC3"
                      icon={faPenToSquare}
                      className="detail-icon clickable"
                      onClick={(event) => editContent(event)}
                    ></FontAwesomeIcon>
                    {category !== 'user' && (
                      <AlertDialog
                        id={item._id}
                        action={() =>
                          handleDelete({
                            id: item._id,
                            collection: category === 'person' ? 'people' : category + 's',
                            verticalId: vertical_id,
                            bookmarked: item?.isBookmarked,
                          })
                        }
                        index={-1}
                        dialogTitle={`Delete ${category}: ${item?.name ? item?.name : item?.title}`}
                        dialogContentText={`This action cannot be undone. This will permanently delete the ${category} and remove the data from our servers.`}
                        color="blue"
                      />
                    )}
                  </div>
                )}
                {item?.status === 'approved' &&
                  (item?.isBookmarked ? (
                    <BookmarkIcon
                      className="detail-icon clickable"
                      onClick={(e) => toggleBookmark(e, item._id)}
                      style={{ color: '#356AC3' }}
                    />
                  ) : (
                    <BookmarkBorderIcon
                      className="detail-icon clickable"
                      onClick={(e) => toggleBookmark(e, item._id, category)}
                      style={{ color: '#356AC3' }}
                    />
                  ))}
              </div>
            </div>

            {
              /* Rendering field with icons */
              Object.keys(field_icon).map(
                (key) =>
                  item?.[key] && (
                    <div className="detail-item" key={key}>
                      {field_icon[key]}
                      <span>{item[key]}</span>
                    </div>
                  )
              )
            }

            <div style={{ display: 'flex', flexDirection: 'row' }}>
              {
                /* Rendering url icons */
                url_icon &&
                  Object.keys(url_icon).map(
                    (key) =>
                      item?.[key] && (
                        <span className="detail-item" key={key}>
                          <a
                            target="_blank"
                            href={
                              item[key].startsWith('http://') || item[key].startsWith('https://')
                                ? item[key]
                                : `https://${item[key]}`
                            }
                          >
                            {url_icon[key]}
                          </a>
                        </span>
                      )
                  )
              }
            </div>

            {importantDate && item?.[importantDate.field] && (
              <div
                className="detail-item"
                style={{
                  border: '1px solid #356AC3',
                  borderRadius: '4px',
                  color: '#356AC3',
                  fontWeight: 700,
                  display: 'flex',
                  paddingRight: '20px',
                }}
              >
                <EventAvailableOutlinedIcon className="detail-icon-" />
                <span>
                  {importantDate.text}: {item[importantDate.field]}
                </span>
              </div>
            )}

            {externalLink && item?.[externalLink.field] && (
              <Button
                variant="outlined"
                disableRipple={true}
                sx={activeButtonStyle}
                onClick={() => {
                  const url = item[externalLink.field];
                  const prefixedUrl =
                    url.startsWith('http://') || url.startsWith('https://')
                      ? url
                      : `https://${url}`;
                  window.open(prefixedUrl, '_blank');
                }}
              >
                {externalLink.text}
              </Button>
            )}

            {
              /* Rendering field with headings */
              Object.keys(field_heading).map(
                (key) =>
                  item?.[key] && (
                    <div style={{ padding: '0 10px' }} key={key}>
                      {/* {field_icon[key]}
                      <span>{item[key]}</span> */}

                      <h2 className="detail-title">{field_heading[key]}</h2>
                      <p style={{ margin: 0, textAlign: 'justify', whiteSpace: 'pre-line' }}>
                        {item[key]}
                      </p>
                    </div>
                  )
              )
            }
          </div>
        </div>
      )}
    </div>
  );
};
export default DetailPage;
